import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Button,
  Chip,
  LinearProgress,
  Stack,
  Typography,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import {
  describeAdvancedReadinessReason,
  type AdvancedReadinessSafeRecord,
} from '../advancedReadiness';
import { ExperimentsApiClient, ExperimentsApiError } from '../api/experimentsClient';
import { EXPERIMENTS_TEST_IDS } from '../experimentsTestIds';

export interface AdvancedReadinessReader {
  getAdvancedReadiness(
    projectId: string,
    experimentId: string,
    signal?: AbortSignal,
  ): Promise<AdvancedReadinessSafeRecord | null>;
}

interface AdvancedReadinessPanelProps {
  projectId: string;
  experimentId: string;
  client?: AdvancedReadinessReader;
}

type LoadState =
  | { kind: 'idle' }
  | { kind: 'loading' }
  | { kind: 'loaded'; record: AdvancedReadinessSafeRecord | null }
  | { kind: 'disabled' }
  | { kind: 'restricted' }
  | { kind: 'error' };

const MAX_VISIBLE_BLOCKERS = 5;

export const AdvancedReadinessPanel: React.FC<AdvancedReadinessPanelProps> = ({
  projectId,
  experimentId,
  client: injectedClient,
}) => {
  const defaultClient = useMemo(() => new ExperimentsApiClient(), []);
  const client = injectedClient ?? defaultClient;
  const [expanded, setExpanded] = useState(false);
  const [loadState, setLoadState] = useState<LoadState>({ kind: 'idle' });
  const loadingRef = useRef(false);
  const abortRef = useRef<AbortController | null>(null);

  useEffect(() => {
    abortRef.current?.abort();
    loadingRef.current = false;
    setExpanded(false);
    setLoadState({ kind: 'idle' });
    return () => abortRef.current?.abort();
  }, [projectId, experimentId]);

  const load = useCallback(async () => {
    if (loadingRef.current) return;
    loadingRef.current = true;
    abortRef.current?.abort();
    const controller = new AbortController();
    abortRef.current = controller;
    setLoadState({ kind: 'loading' });
    try {
      const record = await client.getAdvancedReadiness(projectId, experimentId, controller.signal);
      if (!controller.signal.aborted) setLoadState({ kind: 'loaded', record });
    } catch (error) {
      if (controller.signal.aborted) return;
      if (error instanceof ExperimentsApiError && error.code === 'capability_not_activated') {
        setLoadState({ kind: 'disabled' });
      } else if (error instanceof ExperimentsApiError && (error.status === 401 || error.status === 403)) {
        setLoadState({ kind: 'restricted' });
      } else {
        setLoadState({ kind: 'error' });
      }
    } finally {
      if (abortRef.current === controller) abortRef.current = null;
      loadingRef.current = false;
    }
  }, [client, projectId, experimentId]);

  const chip = readinessChip(loadState);
  const record = loadState.kind === 'loaded' ? loadState.record : null;

  return (
    <Accordion
      disableGutters
      elevation={0}
      expanded={expanded}
      onChange={(_, nextExpanded) => {
        setExpanded(nextExpanded);
        if (nextExpanded && loadState.kind === 'idle') void load();
      }}
      sx={{
        mt: 1.25,
        borderTop: 1,
        borderColor: 'divider',
        backgroundColor: 'transparent',
        '&::before': { display: 'none' },
      }}
      data-testid={EXPERIMENTS_TEST_IDS.advancedReadinessPanel(experimentId)}
    >
      <AccordionSummary
        expandIcon={<ExpandMoreIcon />}
        aria-controls={`advanced-readiness-content-${experimentId}`}
        id={`advanced-readiness-summary-${experimentId}`}
        data-testid={EXPERIMENTS_TEST_IDS.advancedReadinessSummary(experimentId)}
        sx={{ px: 0, minHeight: 44, '& .MuiAccordionSummary-content': { my: 0.75 } }}
      >
        <Stack direction="row" alignItems="center" spacing={1} sx={{ width: '100%', pr: 1 }}>
          <Typography variant="body2" sx={{ flex: 1, fontWeight: 500 }}>
            Advanced readiness
          </Typography>
          <Chip
            size="small"
            variant="outlined"
            label={chip.label}
            color={chip.color}
            data-testid={EXPERIMENTS_TEST_IDS.advancedReadinessStatus(experimentId)}
          />
        </Stack>
      </AccordionSummary>
      <AccordionDetails
        id={`advanced-readiness-content-${experimentId}`}
        sx={{ px: 0, pt: 0.5, pb: 1 }}
        data-testid={EXPERIMENTS_TEST_IDS.advancedReadinessBody(experimentId)}
      >
        {loadState.kind === 'loading' && (
          <Stack spacing={0.75} aria-busy="true">
            <LinearProgress
              aria-label="Loading advanced readiness"
              data-testid={EXPERIMENTS_TEST_IDS.advancedReadinessProgress(experimentId)}
            />
            <Typography variant="caption" color="text.secondary">
              Reading the latest readiness evaluation…
            </Typography>
          </Stack>
        )}

        {loadState.kind === 'loaded' && record === null && (
          <Alert severity="info" variant="outlined">
            No readiness evaluation exists yet. This is not a pass: the current static allocation remains in effect.
          </Alert>
        )}

        {loadState.kind === 'loaded' && record?.state === 'blocked' && (
          <Stack spacing={1}>
            <Alert severity="warning" variant="outlined">
              <strong>Not ready.</strong> Automated gates did not pass. Advanced personalization cannot be activated.
            </Alert>
            <ReadinessFacts record={record} />
            <Stack spacing={0.5} data-testid={EXPERIMENTS_TEST_IDS.advancedReadinessBlockers(experimentId)}>
              <Typography variant="caption" color="text.secondary">Unmet gates</Typography>
              {record.unmetReasons.slice(0, MAX_VISIBLE_BLOCKERS).map((reason, index) => (
                <Typography
                  key={reason}
                  variant="body2"
                  data-testid={EXPERIMENTS_TEST_IDS.advancedReadinessBlocker(experimentId, index)}
                >
                  • {describeAdvancedReadinessReason(reason)}
                </Typography>
              ))}
              {record.unmetReasons.length > MAX_VISIBLE_BLOCKERS && (
                <Typography variant="caption" color="text.secondary">
                  +{record.unmetReasons.length - MAX_VISIBLE_BLOCKERS} additional gates
                </Typography>
              )}
            </Stack>
            <Typography variant="caption" color="text.secondary">
              Sensitive values and internal evidence paths are intentionally omitted.
            </Typography>
          </Stack>
        )}

        {loadState.kind === 'loaded' && record?.state === 'eligible' && (
          <Stack spacing={1}>
            <Alert severity="success" variant="outlined">
              <strong>Eligible for approval.</strong> Passing automated gates does not activate the capability. A separate reviewed approval and certification artifact are required.
            </Alert>
            <ReadinessFacts record={record} />
            <Stack direction="row" spacing={0.75} flexWrap="wrap" useFlexGap>
              <Chip size="small" color="success" variant="outlined" label="11 / 11 gates passed" />
              <Chip size="small" color="warning" variant="outlined" label="Approval required" />
            </Stack>
            <Typography variant="caption" color="text.secondary">
              Current static allocation remains active until a separately authorized rollout.
            </Typography>
          </Stack>
        )}

        {loadState.kind === 'disabled' && (
          <Alert severity="info" variant="outlined">
            Advanced readiness is not activated for this environment or project. Existing experiment behavior is unchanged.
          </Alert>
        )}

        {loadState.kind === 'restricted' && (
          <Alert severity="info" variant="outlined">
            Advanced readiness is not available for this account. Existing experiment behavior is unchanged.
          </Alert>
        )}

        {loadState.kind === 'error' && (
          <Stack spacing={1} alignItems="flex-start">
            <Alert severity="error" variant="outlined">
              Readiness details could not be loaded. No experiment or allocation state was changed.
            </Alert>
            <Button
              size="small"
              variant="outlined"
              onClick={() => void load()}
              data-testid={EXPERIMENTS_TEST_IDS.advancedReadinessRetry(experimentId)}
            >
              Retry read
            </Button>
          </Stack>
        )}
      </AccordionDetails>
    </Accordion>
  );
};

const ReadinessFacts: React.FC<{ record: AdvancedReadinessSafeRecord }> = ({ record }) => (
  <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
    <Typography variant="caption" color="text.secondary">
      Evaluated: {formatEvaluationTime(record.evaluatedAt)}
    </Typography>
    <Typography variant="caption" color="text.secondary">
      Evidence: verified at evaluation
    </Typography>
    <Typography variant="caption" color="text.secondary">
      Fallback: {record.fallback.id} · {record.fallback.version}
    </Typography>
  </Stack>
);

function readinessChip(
  state: LoadState,
): { label: string; color: 'default' | 'success' | 'warning' | 'error' } {
  if (state.kind === 'idle') return { label: 'Check readiness', color: 'default' };
  if (state.kind === 'loading') return { label: 'Loading', color: 'default' };
  if (state.kind === 'disabled' || state.kind === 'restricted') return { label: 'Not available', color: 'default' };
  if (state.kind === 'error') return { label: 'Unavailable', color: 'error' };
  if (state.record === null) return { label: 'No history', color: 'default' };
  if (state.record.state === 'eligible') return { label: 'Eligible', color: 'success' };
  return { label: `Blocked · ${state.record.unmetReasons.length} gates`, color: 'warning' };
}

function formatEvaluationTime(value: string): string {
  return new Intl.DateTimeFormat(undefined, {
    year: 'numeric', month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit',
  }).format(new Date(value));
}
